// Conference Responses pivot: one row per lead, one column per Discovery
// question. Ported from the customer portal; the tenant comes from the global
// operate-as-tenant scope (no tenant → prompt; the backend 422s without it).
import { useState } from 'react';
import { selectTenantPrompt } from '../../copy';
import { Alert, Button, Empty, Input, Skeleton, Space, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useResizableColumns, space } from '@viq-admin/ui';
import {
  fetchConferenceResponses,
  updateConferenceResponse,
  deleteConferenceResponse,
} from '../../api/conferenceResponses';
import { renderAnswerCell, ResponseEditModal, type DiscoveryAnswerBody } from '@viq-admin/screens';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { useAbility } from '../../ability/AbilityContext';
import type {
  ConferenceDiscoveryQuestion,
  ConferenceLead,
  ConferenceLeadAnswer,
} from '../../api/discoveryTypes';

export interface ConferenceResponsesProps {
  conferenceId: string;
}

const BLANK_FILTER = '__blank__';
const OTHER_FILTER = '__other__';

type EditCellHandler = (row: ConferenceLead, q: ConferenceDiscoveryQuestion) => void;

function buildColumns(
  questions: ConferenceDiscoveryQuestion[],
  onEditCell: EditCellHandler,
  canEdit: boolean,
  canDelete: boolean,
): ColumnsType<ConferenceLead> {
  const leftCols: ColumnsType<ConferenceLead> = [
    {
      title: 'Lead',
      dataIndex: 'name',
      fixed: 'left',
      width: 200,
      render: (name: string, row) => <Link to={`/leads/${row.personId}`}>{name}</Link>,
    },
    {
      title: 'Company',
      dataIndex: 'company',
      fixed: 'left',
      width: 160,
    },
  ];

  const questionCols: ColumnsType<ConferenceLead> = questions.map((q) => {
    const isFreeText = q.type === 'free_text';
    return {
      title: q.text,
      key: q.id,
      width: 220,
      render: (_: unknown, row: ConferenceLead) =>
        canEdit ? (
          <div
            style={{ cursor: 'pointer' }}
            onClick={() => onEditCell(row, q)}
            title={
              canDelete
                ? 'Click to edit / delete this response'
                : 'Click to edit this response'
            }
          >
            {renderAnswerCell(row.answers[q.id], q)}
          </div>
        ) : (
          <div>{renderAnswerCell(row.answers[q.id], q)}</div>
        ),
      ...(isFreeText
        ? {
            filterDropdown: ({
              setSelectedKeys,
              selectedKeys,
              confirm,
            }: {
              setSelectedKeys: (keys: React.Key[]) => void;
              selectedKeys: React.Key[];
              confirm: () => void;
            }) => (
              <div style={{ padding: space.sm }}>
                <Input
                  placeholder="Contains…"
                  value={String(selectedKeys[0] ?? '')}
                  onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                  onPressEnter={confirm}
                  style={{ width: 180 }}
                  aria-label={`Filter ${q.text}`}
                />
              </div>
            ),
            onFilter: (value: React.Key | boolean, row: ConferenceLead) => {
              const a = row.answers[q.id];
              return !!a?.freeText?.toLowerCase().includes(String(value).toLowerCase());
            },
          }
        : {
            filters: [
              ...q.options.map((o) => ({ text: o.text, value: o.id })),
              ...(q.hasOtherOption ? [{ text: '(Other)', value: OTHER_FILTER }] : []),
              { text: '(unanswered)', value: BLANK_FILTER },
            ],
            onFilter: (value: React.Key | boolean, row: ConferenceLead) => {
              const a = row.answers[q.id];
              const v = String(value);
              if (v === BLANK_FILTER) {
                return (
                  !a ||
                  (a.selectedOptionIds.length === 0 &&
                    (!a.freeText || a.freeText.trim() === ''))
                );
              }
              if (v === OTHER_FILTER) {
                return !!a && !!a.freeText && a.freeText.trim() !== '';
              }
              return !!a?.selectedOptionIds.includes(v);
            },
          }),
    };
  });

  const rightCols: ColumnsType<ConferenceLead> = [
    {
      title: 'Captured',
      dataIndex: 'capturedAt',
      width: 180,
      render: (iso: string) => new Date(iso).toLocaleString(),
      defaultSortOrder: 'descend',
      sorter: (a: ConferenceLead, b: ConferenceLead) => a.capturedAt.localeCompare(b.capturedAt),
    },
  ];

  return [...leftCols, ...questionCols, ...rightCols];
}

export function ConferenceResponses({ conferenceId }: ConferenceResponsesProps) {
  const { companyId } = useCompanyScope();
  const hasScope = companyId != null;
  const ability = useAbility();
  // The backend gates update and delete on this pivot SEPARATELY —
  // updateResponseHandler → requireAbility('update', 'Lead'),
  // deleteResponseHandler → requireAbility('delete', 'Lead')
  // (viq-backend apps/superadmin-console/src/handlers/conferenceResponses.ts:88-89)
  // — mirroring the three-gate split in Leads/LeadDetailBody.tsx (leads-only
  // edit carve-out, viq-admin#13 / viq-backend#52). super_admin_readonly holds
  // `update Lead` via that carve-out but not `manage`/`delete`, so a single
  // `manage Lead` flag here would hide the edit affordance the backend would
  // actually accept for it.
  const canEdit = ability.can('update', 'Lead');
  const canDelete = ability.can('delete', 'Lead');

  const [editing, setEditing] = useState<{
    personId: string;
    leadName: string;
    question: ConferenceDiscoveryQuestion;
    answer: ConferenceLeadAnswer | undefined;
  } | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['conferenceResponses', conferenceId, companyId],
    queryFn: () => fetchConferenceResponses(conferenceId, companyId as string),
    enabled: !!conferenceId && hasScope,
  });

  // Built unconditionally (rules of hooks): empty questions → just left/right cols.
  const baseColumns = buildColumns(
    data?.questions ?? [],
    (row, q) =>
      setEditing({
        personId: row.personId,
        leadName: row.name,
        question: q,
        answer: row.answers[q.id],
      }),
    canEdit,
    canDelete,
  );
  const { columns, components } = useResizableColumns('conferenceResponses', baseColumns);

  if (!hasScope) {
    return (
      <Empty
        description={selectTenantPrompt("view this conference's Responses")}
        style={{ padding: '48px 0' }}
      />
    );
  }
  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (error) return <Alert type="error" message="Couldn't load Responses" showIcon />;
  if (!data) return null;

  if (data.questions.length === 0 && data.leads.length === 0) {
    return <Empty description="No questions or leads yet for this conference" />;
  }
  if (data.leads.length === 0) {
    return <Empty description="No leads have been captured at this conference yet" />;
  }
  if (data.questions.length === 0) {
    return <Empty description="Questions aren't set up for this conference" />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {data.truncated ? (
        <Alert
          type="warning"
          showIcon
          message="Showing the most recent 1,000 leads. Server-side pagination is a fast-follow — file an issue if you hit this."
        />
      ) : null}
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Text type="secondary">
          {data.leads.length} leads · {data.questions.length} questions
        </Typography.Text>
        <Tooltip title="Coming soon">
          <Button disabled>Download CSV</Button>
        </Tooltip>
      </Space>
      <Table<ConferenceLead>
        rowKey="personId"
        columns={columns}
        components={components}
        dataSource={data.leads}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
      />
      {editing ? (
        <ResponseEditModal
          key={`${editing.personId}:${editing.question.id}`}
          open
          leadName={editing.leadName}
          question={editing.question}
          answer={editing.answer}
          onSave={(body: DiscoveryAnswerBody) =>
            updateConferenceResponse(
              conferenceId,
              editing.personId,
              editing.question.id,
              body,
              companyId as string,
            )
          }
          onDelete={
            canDelete
              ? () =>
                  deleteConferenceResponse(
                    conferenceId,
                    editing.personId,
                    editing.question.id,
                    companyId as string,
                  )
              : undefined
          }
          onClose={() => setEditing(null)}
          onSaved={() => refetch()}
        />
      ) : null}
    </Space>
  );
}
